"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const client_library_1 = require("@seneca/client-library");
const course_service_types_1 = require("@seneca/course-service-types");
const CourseDoesNotExistError_1 = __importDefault(require("./errors/CourseDoesNotExistError"));
const CourseEnrollmentBodyNotValidError_1 = __importDefault(require("./errors/CourseEnrollmentBodyNotValidError"));
const CourseEnrollmentDoesNotExistError_1 = __importDefault(require("./errors/CourseEnrollmentDoesNotExistError"));
const CourseForbiddenError_1 = __importDefault(require("./errors/CourseForbiddenError"));
const DraftBodyNotValidError_1 = __importDefault(require("./errors/DraftBodyNotValidError"));
const DraftDoesNotExistError_1 = __importDefault(require("./errors/DraftDoesNotExistError"));
const DraftObjectConflictError_1 = __importDefault(require("./errors/DraftObjectConflictError"));
const DraftObjectDoesNotExistError_1 = __importDefault(require("./errors/DraftObjectDoesNotExistError"));
const DraftObjectsNotFoundError_1 = __importDefault(require("./errors/DraftObjectsNotFoundError"));
const InvalidParamsError_1 = __importDefault(require("./errors/InvalidParamsError"));
const PastPapersDoNotExistError_1 = __importDefault(require("./errors/PastPapersDoNotExistError"));
const PublishingErrors_1 = __importDefault(require("./errors/PublishingErrors"));
const SectionDoesNotExistError_1 = __importDefault(require("./errors/SectionDoesNotExistError"));
const SubscriptionNotActiveError_1 = __importDefault(require("./errors/SubscriptionNotActiveError"));
const SubscriptionNotFoundError_1 = __importDefault(require("./errors/SubscriptionNotFoundError"));
const SubscriptionNotValidError_1 = __importDefault(require("./errors/SubscriptionNotValidError"));
const UnauthorizedError_1 = __importDefault(require("./errors/UnauthorizedError"));
const WordNotFoundError_1 = __importDefault(require("./errors/WordNotFoundError"));
const WordsNotFoundError_1 = __importDefault(require("./errors/WordsNotFoundError"));
const models_1 = require("./models");
const DEFAULT_FETCH_ALL_LIMIT = 3000;
/**
 * CourseServiceClient
 * @param {Object} options options object input data and dependency injection
 * @param {Object} options.fetch change fetch method from whatwg-fetch (optional, default: whatwg-fetch)
 * @param {string} options.courseId saves entering courseId required for some methods
 * @param {string} options.baseCdnUrl the base URL for the course service CDN
 * @param {string} options.protocol the http protocol of the service endpoint
 * @param {string} options.host the host of the service endpoint
 * @param {string} options.path the path of the service endpoint (optional)
 * @param {number} options.retries the number of times the client should retry on network error / service error (optional, default: 3)
 * @param {number} options.delayInSecs the delay between retries (optional, default: 2)
 * @param {Object} options.credentials identifiers and secrets required to hit protected endpoints (optional)
 * @param {string} options.region states the region of the service for signing (optional, default: eu-west-1)
 * @param {RequestMaker} options.requestMaker for overriding request making logic (optional, default: RequestMaker)
 * @throws {UrlConstructionError} when either options.protocol or options.host is missing
 */
class CourseServiceClient extends client_library_1.Client {
    constructor(fetch, options) {
        super(fetch, options);
        if (!options.baseCdnUrl) {
            throw new client_library_1.MissingParamError('baseCdnUrl');
        }
        this.courseId = this.options.courseId;
        this.baseCdnUrl = this.options.baseCdnUrl;
    }
    async createDraftObject({ type, object, position, courseId, id, region }) {
        this.validateMissingParams({ 'course id': courseId });
        const url = `${this.url}/api/drafts/${courseId}/id/${id}`;
        const expectedResponses = [201, 404, 409];
        const body = {
            type,
            object,
            position
        };
        const response = await this.requestMaker.makeRequest(url, {
            method: 'PUT',
            body: JSON.stringify(body),
            region
        }, expectedResponses);
        if (response.status === 404) {
            throw new DraftObjectDoesNotExistError_1.default(courseId, object.parentId);
        }
        if (response.status === 409) {
            throw new DraftObjectConflictError_1.default(courseId, id, object.parentId, 'create');
        }
        // else 201 created
    }
    /**
     * Creates draft course (requires content-creators group)
     * @param {Object} course
     * @param {string} course.id (suggested === courseId)
     * @param {string} courseId
     * @throws {MissingParamError} when course object is missing
     * @throws {MissingParamError} when course id object is missing
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<undefined>}
     */
    async createDraftCourse(course, courseId = this.courseId) {
        this.validateMissingParams({ course });
        let region;
        if (course.regions && course.regions.length > 0) {
            region = course.regions[0];
        }
        await this.createDraftObject({
            type: 'course',
            object: course,
            courseId,
            id: courseId,
            region
        });
    }
    /**
     * Creates draft section (requires content-creators group)
     * @param {Object} section
     * @param {string} section.id
     * @param {Number} position to place in the parent's array of child ids
     * @param {string} courseId
     * @throws {MissingParamError} when section object is missing
     * @throws {MissingParamError} when course id object is missing
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<undefined>}
     */
    async createDraftSection(section, position, courseId = this.courseId) {
        this.validateMissingParams({ section });
        if (position === null || position === undefined) {
            throw new client_library_1.MissingParamError('position');
        }
        await this.createDraftObject({
            type: 'section',
            object: section,
            courseId,
            id: section.id,
            position
        });
    }
    /**
     * Creates draft content (requires content-creators group)
     * @param {Object} content
     * @param {string} content.id
     * @param {Number} position to place in the parent's array of child ids
     * @param {string} courseId
     * @throws {MissingParamError} when content object is missing
     * @throws {MissingParamError} when course id object is missing
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<undefined>}
     */
    async createDraftContent(content, position, courseId = this.courseId) {
        this.validateMissingParams({ content });
        if (position === null || position === undefined) {
            throw new client_library_1.MissingParamError('position');
        }
        await this.createDraftObject({
            type: 'content',
            object: content,
            courseId,
            id: content.id,
            position
        });
    }
    /**
     * Creates draft content module (requires content-creators group)
     * @param {Object} module
     * @param {string} module.id
     * @param {Number} position to place in the parent's array of child ids
     * @param {string} courseId
     * @throws {MissingParamError} when module object is missing
     * @throws {MissingParamError} when course id object is missing
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<undefined>}
     */
    async createDraftContentModule(module, position, courseId = this.courseId) {
        this.validateMissingParams({ module });
        if (position === null || position === undefined) {
            throw new client_library_1.MissingParamError('position');
        }
        await this.createDraftObject({
            type: 'module',
            object: module,
            courseId,
            id: module.id,
            position
        });
    }
    async updateDraftObject({ type, object, courseId, id }) {
        this.validateMissingParams({ 'course id': courseId });
        const url = `${this.url}/api/drafts/${courseId}/id/${id}`;
        const expectedResponses = [200, 404];
        const body = {
            object,
            type
        };
        const response = await this.requestMaker.makeRequest(url, {
            method: 'PATCH',
            body: JSON.stringify(body)
        }, expectedResponses);
        if (response.status === 404) {
            throw new DraftObjectDoesNotExistError_1.default(courseId, object.id);
        }
    }
    /**
     * Updates draft course (requires content-creators group)
     * @param {Object} course
     * @param {string} courseId
     * @throws {MissingParamError} when course object is missing
     * @throws {MissingParamError} when course id object is missing
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<undefined>}
     */
    async updateDraftCourse(course, courseId) {
        this.validateMissingParams({ course });
        await this.updateDraftObject({
            type: 'course',
            object: course,
            courseId,
            id: courseId
        });
    }
    /**
     * Updates draft section (requires content-creators group)
     * @param {Object} section
     * @param {string} section.id
     * @param {string} courseId
     * @throws {MissingParamError} when section object is missing
     * @throws {MissingParamError} when course id object is missing
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<undefined>}
     */
    async updateDraftSection(section, courseId = this.courseId) {
        this.validateMissingParams({ section });
        await this.updateDraftObject({
            type: 'section',
            object: section,
            courseId,
            id: section.id
        });
    }
    /**
     * Updates draft content (requires content-creators group)
     * @param {Object} content
     * @param {string} content.id
     * @param {string} courseId
     * @throws {MissingParamError} when content object is missing
     * @throws {MissingParamError} when course id object is missing
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<undefined>}
     */
    async updateDraftContent(content, courseId = this.courseId) {
        this.validateMissingParams({ content });
        await this.updateDraftObject({
            type: 'content',
            object: content,
            courseId,
            id: content.id
        });
    }
    /**
     * Updates draft content module (requires content-creators group)
     * @param {Object} module
     * @param {string} module.id
     * @param {string} courseId
     * @throws {MissingParamError} when module object is missing
     * @throws {MissingParamError} when course id object is missing
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<undefined>}
     */
    async updateDraftContentModule(module, courseId = this.courseId) {
        this.validateMissingParams({ module });
        await this.updateDraftObject({
            type: 'module',
            object: module,
            courseId,
            id: module.id
        });
    }
    /**
     * Publishes draft course into live catalogue (requires content-admin group)
     * @param {string} courseId
     * @throws {PublishingErrors} when there are publishing errors
     * @throws {DraftDoesNotExistError} when draft does not exist
     * @throws {MissingParamError} when course id object is missing
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<Response>} this can be ignored, but maybe useful for logging
     */
    async publish(courseId = this.courseId) {
        this.validateMissingParams({ 'course id': courseId });
        const expectedResponses = [200, 409, 404];
        const response = await this.requestMaker.makeRequest(`${this.url}/api/drafts/${courseId}/publish`, { method: 'PUT' }, expectedResponses);
        if (response.status === 200) {
            return;
        }
        if (response.status === 409) {
            const { errors } = response.json;
            throw new PublishingErrors_1.default(courseId, errors);
        }
        if (response.status === 404) {
            throw new DraftDoesNotExistError_1.default(courseId);
        }
    }
    generateUrlWithParams({ url, limit, lastKey }) {
        const params = this._generatePagingQueryParams({ limit, lastKey });
        if (params.length !== 0) {
            url += `?${params.join('&')}`;
        }
        return url;
    }
    /**
     * Fetch draft course (requires content-creators group)
     * @param {string} courseId courseId
     * @param {number} options.limit the limit of course objects to return in each pagination response
     * @throws {MissingParamError} when there is no course id
     * @throws {DraftDoesNotExistError} when draft does not exist
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<Draft>}
     */
    async fetchDraft(courseId = this.courseId, { limit = DEFAULT_FETCH_ALL_LIMIT } = {}) {
        this.validateMissingParams({ 'course id': courseId });
        const baseUrl = `${this.url}/api/drafts/${courseId}`;
        let course;
        let sections = [];
        let contents = [];
        let contentModules = [];
        let lastKey = undefined;
        do {
            const url = this.generateUrlWithParams({
                url: baseUrl,
                limit,
                lastKey
            });
            const expectedResponses = [200, 404];
            const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
            if (response.status === 404) {
                throw new DraftDoesNotExistError_1.default(courseId);
            }
            const page = response.json;
            lastKey = page.lastKey;
            course = page.course;
            sections = sections.concat(page.sections);
            contents = contents.concat(page.contents);
            contentModules = contentModules.concat(page.contentModules);
        } while (lastKey);
        return new models_1.Draft({
            course,
            sections,
            contents,
            contentModules
        });
    }
    /**
     * Fetches a signed url for a course or section
     * @param {string} courseId the id of the course to fetch
     * @param {string} sectionId (optional) the id of the section to fetch
     * @param {ContentType[]} contentTypes (optional) the types of content to fetch
     */
    async fetchSignedUrl(courseId = this.courseId, sectionId, contentTypes) {
        this.validateMissingParams({ 'course id': courseId });
        const queryParamsParts = [];
        if (sectionId) {
            queryParamsParts.push(`sectionId=${sectionId}`);
        }
        if (contentTypes) {
            queryParamsParts.push(`contentTypes=${contentTypes.join(',')}`);
        }
        const queryParams = queryParamsParts.join('&');
        const url = `${this.url}/api/courses/${courseId}/signed-url?${queryParams}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, [200, 403, 404]);
        if (response.status === 403) {
            const { reason } = response.json;
            return this.throwSpecificInvalidSubscriptionError(reason);
        }
        return response;
    }
    /**
     * Fetches a course
     * @param {string} courseId courseId
     * @throws {MissingParamError} when there is no course id
     * @throws {CourseDoesNotExistError} when course does not exist
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<Course>}
     */
    async fetchCourse(courseId = this.courseId) {
        this.validateMissingParams({ 'course id': courseId });
        const signedUrlResponse = await this.fetchSignedUrl(courseId);
        if (signedUrlResponse.status === 404) {
            throw new CourseDoesNotExistError_1.default(courseId);
        }
        const signedCourseUrl = signedUrlResponse.json.url;
        const expectedResponses = [200, 404];
        const response = await this.requestMaker.makeRequest(signedCourseUrl, {
            method: 'GET',
            headers: {
                correlationId: signedUrlResponse.headers.get('correlationId')
            }
        }, expectedResponses);
        if (response.status === 404) {
            throw new CourseDoesNotExistError_1.default(courseId);
        }
        return response.json;
    }
    /**
     * Queries courses with params
     * @param {Object} params
     * @param {string} params.query free-text query
     * @param {Array<COURSE_OBJECT_TYPES>} params.types Array of course entity types to search. Default: search all entities
     * @param {Object} params.filters Fields to match exactly. Object with category as keys and array of fields as values.
     * @param {Array<string>} params.aggregations Array of fields to aggregate on
     * @param {Array<string>} params.sort Array of fields to sort by (provide order with `:asc/desc` eg _score:desc)
     * @param {number} params.from pagination from index
     * @param {number} params.size max results returned
     * @param {boolean} params.includeContent include content when searching
     * @param {Object} options
     * @param {boolean} options.noRegion pass no region header when making the request
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @throws {InvalidParamsError} when params are not valid
     * @returns {Promise.<Object>}
     */
    async queryCourses(params = {}, options = {}) {
        const expectedResponses = [200, 422];
        const response = await this.requestMaker.makeRequest(`${this.url}/api/courses/queryCourses`, {
            method: 'POST',
            body: JSON.stringify(params),
            noRegion: options.noRegion
        }, expectedResponses);
        if (response.status === 422) {
            const body = response.json;
            throw new InvalidParamsError_1.default(body.pointer, body.reason);
        }
        if (response.status === 200) {
            return response.json;
        }
        throw new Error(`Unexpected response: ${response.status} ${response.json}`);
    }
    /**
     * Queries within a course with params
     * @param {Object} params
     * @param {string} params.courseId courseId to search in
     * @param {string} params.query free-text query
     * @param {Array<COURSE_OBJECT_TYPES>} params.types Array of course entity types to search. Default: search all entities
     * @param {Array<string>} params.sort Array of fields to sort by (provide order with `:asc/desc` eg _score:desc)
     * @param {number} params.from pagination from index
     * @param {number} params.size max results returned
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @throws {InvalidParamsError} when params are not valid
     * @throws {MissingParamError} when required params are missing
     * @returns {Promise.<QueryInCourseResponse>}
     */
    async queryInCourse(params) {
        this._validateMissingParams({
            courseId: params.courseId,
            query: params.query
        });
        const { courseId } = params, body = __rest(params, ["courseId"]);
        const expectedResponses = [200, 422];
        const response = await this.requestMaker.makeRequest(`${this.url}/api/courses/${courseId}/query`, {
            method: 'POST',
            body: JSON.stringify(Object.assign({}, body))
        }, expectedResponses);
        if (response.status === 422) {
            const body = response.json;
            throw new InvalidParamsError_1.default(body.pointer, body.reason);
        }
        if (response.status === 200) {
            return response.json;
        }
        throw new Error(`Unexpected response: ${response.status} ${response.json}`);
    }
    /**
     * Fetches suggested courses for user
     * @param {number} limit optional limit to number of courses to return, defaults to 50
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<Object>}
     */
    async fetchSuggestedCourses(limit = 50) {
        const expectedResponses = [200, 429];
        let url = `${this.url}/api/courses/suggestedCourses`;
        if (limit) {
            url = `${url}?limit=${limit}`;
        }
        const response = await this.requestMaker.makeRequest(url, {
            method: 'GET'
        }, expectedResponses);
        if (response.status === 429) {
            throw new client_library_1.InvalidParamError('limit', limit, 'limit must be a number');
        }
        if (response.status === 200) {
            return response.json;
        }
        throw new Error(`Unexpected response: ${response.status} ${response.json}`);
    }
    /**
     * Fetches Leading Modules for a Course
     * @param {string} courseId courseId
     * @throws {MissingParamError} when there is no course id
     * @throws {CourseDoesNotExistError} when course does not exist
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<Course>}
     */
    async fetchLeadingModules(courseId = this.courseId) {
        this.validateMissingParams({ 'course id': courseId });
        const expectedResponses = [200, 404];
        const response = await this.requestMaker.makeRequest(`${this.url}/api/courses/${courseId}/leadingModules`, { method: 'GET' }, expectedResponses);
        if (response.status === 200) {
            return response.json;
        }
        if (response.status === 404) {
            throw new CourseDoesNotExistError_1.default(courseId);
        }
    }
    /**
     * Fetches all sections details for a course, paging as needed to get the full list.
     * @param {string} courseId courseId
     * @throws {MissingParamError} when there is no course id
     * @throws {CourseDoesNotExistError} when course does not exist
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<SectionDetails>}
     */
    async fetchCourseSectionDetails(courseId, { limit = DEFAULT_FETCH_ALL_LIMIT } = {}) {
        this.validateMissingParams({ courseId });
        let course;
        let sections = [];
        let lastKey;
        do {
            const page = await this.fetchPageOfCourseSectionDetails(courseId, {
                limit,
                lastKey
            });
            course = page.course;
            sections = sections.concat(page.sections);
            lastKey = page.lastKey;
        } while (lastKey);
        return { course, sections };
    }
    /**
     * Fetches a single page of section details
     * @param options
     * @param {Number} options.limit the number of details to return (optional, default: 100)
     * @param {Object} options.lastKey the key of the last item of the previous page, enables paging (option, default: null)
     * @throws {MissingParamError} when there is no course id
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<SectionDetails>}
     */
    async fetchPageOfCourseSectionDetails(courseId, { limit, lastKey } = {}) {
        this.validateMissingParams({ courseId });
        const baseUrl = `${this.baseCdnUrl}/api/courses/${courseId}/sections`;
        const expectedResponses = [200, 403, 404];
        const url = this.generateUrlWithParams({
            url: baseUrl,
            limit,
            lastKey
        });
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 403) {
            throw new CourseForbiddenError_1.default(courseId);
        }
        if (response.status === 404) {
            throw new CourseDoesNotExistError_1.default(courseId);
        }
        return response.json;
    }
    /**
     * Fetches a section of a course
     * @param {string} courseId the id of the course which contains the section
     * @param {string} sectionId the id of the section to fetch
     * @param {ContentType[]} contentTypes (optional) the types of content to fetch
     * @throws {MissingParamError} when there is no course id or section id
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<Section>}
     */
    async fetchSection(courseId, sectionId, contentTypes) {
        this.validateMissingParams({ courseId, sectionId });
        const signedUrlResponse = await this.fetchSignedUrl(courseId, sectionId, contentTypes);
        if (signedUrlResponse.status === 404) {
            throw new SectionDoesNotExistError_1.default(courseId, sectionId);
        }
        const signedSectionUrl = signedUrlResponse.json.url;
        const expectedResponses = [200, 404];
        const response = await this.requestMaker.makeRequest(signedSectionUrl, {
            method: 'GET',
            headers: {
                correlationId: signedUrlResponse.headers.get('correlationId')
            }
        }, expectedResponses);
        if (response.status === 404) {
            throw new SectionDoesNotExistError_1.default(courseId, sectionId);
        }
        return response.json;
    }
    /**
     * Fetches all course details paging as needed to get the full list.
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<CourseDetails>}
     */
    async fetchCourseDetails({ limit = DEFAULT_FETCH_ALL_LIMIT, maxVisibility = course_service_types_1.COURSE_VISIBILITY.public, region, noRegion } = {}) {
        let items = [];
        let lastKey = undefined;
        do {
            const page = await this.fetchPageOfCourseDetails({
                limit,
                lastKey,
                maxVisibility,
                region,
                noRegion
            });
            lastKey = page.lastKey;
            items = items.concat(page.courses);
        } while (lastKey);
        return new models_1.CourseDetails({
            courses: items,
            count: items.length
        });
    }
    /**
     * Fetches a single page of course details
     * @param options
     * @param {Number} options.limit the number of details to return (optional, default: 100)
     * @param {Object} options.lastKey the key of the last item of the previous page, enables paging (option, default: null)
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<CourseDetails>}
     */
    async fetchPageOfCourseDetails({ limit = DEFAULT_FETCH_ALL_LIMIT, lastKey, maxVisibility, region, noRegion } = {}) {
        const queryParamsParts = [];
        queryParamsParts.push(`limit=${limit}`);
        if (lastKey) {
            queryParamsParts.push(`lastKey=${encodeURIComponent(lastKey)}`);
        }
        if (maxVisibility) {
            queryParamsParts.push(`maxVisibility=${encodeURIComponent(maxVisibility)}`);
        }
        const queryParams = queryParamsParts.join('&');
        const url = `${this.baseCdnUrl}/api/courses?${queryParams}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'GET', region, noRegion }, [200]);
        return new models_1.CourseDetails(response.json);
    }
    /**
     * Fetches all draft details paging as needed to get the full list. (requires content-creators group)
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<CourseDetails>}
     */
    async fetchDraftDetails({ limit = DEFAULT_FETCH_ALL_LIMIT, maxVisibility = course_service_types_1.COURSE_VISIBILITY.public, region } = {}) {
        let items = [];
        let lastKey = undefined;
        do {
            const page = await this.fetchPageOfDraftDetails({
                limit,
                lastKey,
                maxVisibility,
                region
            });
            lastKey = page.lastKey;
            items = items.concat(page.courses);
        } while (lastKey);
        return new models_1.CourseDetails({
            courses: items,
            count: items.length
        });
    }
    /**
     * Fetches a single page of draft details (requires content-creators group)
     * @param options
     * @param {Number} options.limit the number of details to return (optional, default: 100)
     * @param {Object} options.lastKey the key of the last item of the previous page, enables paging (option, default: null)
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<CourseDetails>}
     */
    async fetchPageOfDraftDetails({ limit = DEFAULT_FETCH_ALL_LIMIT, lastKey, maxVisibility, region } = {}) {
        const queryParamsParts = [];
        queryParamsParts.push(`limit=${limit}`);
        if (lastKey) {
            queryParamsParts.push(`lastKey=${encodeURIComponent(lastKey)}`);
        }
        if (maxVisibility) {
            queryParamsParts.push(`maxVisibility=${encodeURIComponent(maxVisibility)}`);
        }
        const queryParams = queryParamsParts.join('&');
        const url = `${this.url}/api/drafts?${queryParams}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'GET', region }, [200]);
        return new models_1.CourseDetails(response.json);
    }
    /**
     * Deletes draft course (requires content-admin group)
     * @param {string} courseId
     * @throws {MissingParamError} when a parameter is missing
     * @throws {DraftObjectDoesNotExistError} when the draft course does not exist
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     */
    async deleteDraftCourse(courseId) {
        this.validateMissingParams({ courseId });
        await this._makeDraftRequest(courseId, null, null, 'DELETE', null, [204, 404]);
    }
    async _makeDraftRequest(courseId, id, type, method, body, expectedResponses) {
        let url = `${this.url}/api/drafts`;
        if (courseId) {
            url += `/${courseId}`;
        }
        if (id) {
            url += `/id/${id}`;
        }
        if (type) {
            url += `/${type}`;
        }
        const response = await this.requestMaker.makeRequest(url, {
            method: method,
            body: body ? JSON.stringify(body) : undefined
        }, expectedResponses);
        if (response.status === 404) {
            throw new DraftObjectDoesNotExistError_1.default(courseId, id || courseId);
        }
        if (response.status === 409) {
            throw new DraftObjectConflictError_1.default(courseId, id, body.newParentId, type);
        }
        if (response.status === 422) {
            throw new DraftBodyNotValidError_1.default(body);
        }
        return response.json;
    }
    /**
     * Moves a draft object (and its children) to a new location within a course (requires content-creators group)
     * @param {string} courseId
     * @param {string} id of the object to move
     * @param {string} newParentId - the id of the parent to move the object to
     * @param {number} position to place object within the array of ids in the new parent
     * @throws {MissingParamError} when a parameter is missing
     * @throws {DraftObjectDoesNotExistError} when the draft object does not exist
     * @throws {DraftObjectConflictError} for an invalid operation
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<DraftObject>} the updated object after the move
     */
    async moveDraftObject(courseId, id, newParentId, position) {
        this.validateMissingParams({ courseId, id, newParentId });
        if (position === null || position === undefined) {
            throw new client_library_1.MissingParamError('position');
        }
        return await this._makeDraftRequest(courseId, id, 'move', 'POST', { newParentId, position }, [200, 404, 409]);
    }
    /**
     * Deletes a draft object (and its children if they exist) (requires content-creators group)
     * @param {string} courseId
     * @param {string} id of the object to delete
     * @throws {MissingParamError} when a parameter is missing
     * @throws {DraftObjectDoesNotExistError} when the draft object does not exist
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<undefined>}
     */
    async deleteDraftObject(courseId, id) {
        this.validateMissingParams({ courseId, id });
        await this._makeDraftRequest(courseId, id, null, 'DELETE', null, [200, 404]);
    }
    /**
     * Batch imports (copies) multiple draft objects and their children to a new location in the
     * same course or a different course (requires content-creators group)
     * @param {Array<{courseId, id}>} srcKeys array of keys to import
     * @param {string} destCourseId the course id to import the objects to
     * @param {string} newParentId - the id of the parent to import the objects to
     * @param {number} position to place objects within the array of ids in the new parent
     * @throws {MissingParamError} when a parameter is missing
     * @throws {DraftObjectDoesNotExistError} when the draft object does not exist
     * @throws {DraftObjectConflictError} for an invalid operation
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<DraftObject>} the updated object after the import
     */
    async batchImportDraftObjects(srcKeys, destCourseId, newParentId, position) {
        this.validateMissingParams({ srcKeys, destCourseId, newParentId });
        if (position === null || position === undefined) {
            throw new client_library_1.MissingParamError('position');
        }
        return await this._makeDraftRequest(null, null, 'import/batch', 'POST', { srcKeys, destCourseId, newParentId, position }, [200, 404, 409]);
    }
    /**
     * Updates draft to the desired visibility (requires content-admin group)
     * @param {string} courseId the id of the course to update
     * @param {string<COURSE_VISIBILITY>} visibility the visibility to set the draft to
     * @throws {DraftObjectDoesNotExistError} when the draft object does not exist
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<DraftObject>} the updated object after the visibility change
     */
    async updateDraftVisibility(courseId, visibility) {
        this.validateMissingParams({ courseId, visibility });
        return this._makeDraftRequest(courseId, null, 'visibility', 'PATCH', { visibility }, [200, 404, 422]);
    }
    /**
     * Sets a draft to visibility: public (requires content-admin group)
     * @param {string} courseId the id of the course to update
     * @param {string<COURSE_VISIBILITY>} visibility the visibility to set the draft to
     * @throws {DraftObjectDoesNotExistError} when the draft object does not exist
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<DraftObject>} the updated object after the visibility change
     */
    async makeDraftPublic(courseId) {
        this.validateMissingParams({ courseId });
        return this.updateDraftVisibility(courseId, course_service_types_1.COURSE_VISIBILITY.public);
    }
    /**
     * Sets a draft to visibility: hidden (requires content-admin group)
     * @param {string} courseId the id of the course to update
     * @param {string<COURSE_VISIBILITY>} visibility the visibility to set the draft to
     * @throws {DraftObjectDoesNotExistError} when the draft object does not exist
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<DraftObject>} the updated object after the visibility change
     */
    async makeDraftHidden(courseId) {
        this.validateMissingParams({ courseId });
        return this.updateDraftVisibility(courseId, course_service_types_1.COURSE_VISIBILITY.hidden);
    }
    /**
     * Updates draft to the desired subscription tier (requires content-admin group)
     * @param {string} courseId the id of the course to update
     * @param {string<COURSE_SUBSCRIPTION_TIERS>} subscriptionTier the subscription tier to set the draft to
     * @throws {DraftObjectDoesNotExistError} when the draft object does not exist
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<DraftObject>} the updated object after the subscription tier change
     */
    async updateDraftSubscriptionTier(courseId, subscriptionTier) {
        this.validateMissingParams({ courseId, subscriptionTier });
        return this._makeDraftRequest(courseId, null, 'subscription-tier', 'PATCH', { subscriptionTier }, [200, 404, 422]);
    }
    /**
     * Sets a draft to subscriptionTier: free (requires content-admin group)
     * @param {string} courseId the id of the course to update
     * @param {string<COURSE_SUBSCRIPTION_TIERS>} subscriptionTier the subscription tier to set the draft to
     * @throws {DraftObjectDoesNotExistError} when the draft object does not exist
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<DraftObject>} the updated object after the subscription tier change
     */
    async makeDraftSubscriptionFree(courseId) {
        this.validateMissingParams({ courseId });
        return this.updateDraftSubscriptionTier(courseId, course_service_types_1.COURSE_SUBSCRIPTION_TIERS.free);
    }
    /**
     * Sets a draft to subscriptionTier: premium (requires content-admin group)
     * @param {string} courseId the id of the course to update
     * @param {string<COURSE_SUBSCRIPTION_TIERS>} subscriptionTier the subscription tier to set the draft to
     * @throws {DraftObjectDoesNotExistError} when the draft object does not exist
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<DraftObject>} the updated object after the subscription tier change
     */
    async makeDraftSubscriptionPremium(courseId) {
        this.validateMissingParams({ courseId });
        return this.updateDraftSubscriptionTier(courseId, course_service_types_1.COURSE_SUBSCRIPTION_TIERS.premium);
    }
    async _makeCourseEnrollmentRequest(method, courseId, body, queryParams) {
        let url = this.url + '/api/enrollments/me/courses';
        if (courseId) {
            url += `/${courseId}`;
        }
        if (queryParams && Object.keys(queryParams).length > 0) {
            const queryParamParts = Object.keys(queryParams).map(param => `${encodeURIComponent(param)}=${encodeURIComponent(queryParams[param])}`);
            url += `?${queryParamParts.join('&')}`;
        }
        const expectedResponses = [200, 201, 204, 400, 403, 404, 422];
        const response = await this.requestMaker.makeRequest(url, {
            method,
            body: body ? JSON.stringify(body) : undefined
        }, expectedResponses);
        if (response.status === 403) {
            const body = response.json;
            return this.throwSpecificInvalidSubscriptionError(body.reason);
        }
        if (response.status === 404) {
            throw new CourseEnrollmentDoesNotExistError_1.default(courseId);
        }
        if (response.status === 400 || response.status === 422) {
            const body = response.json;
            throw new CourseEnrollmentBodyNotValidError_1.default(body.pointer, body.reason);
        }
        if (response.status === 200 || response.status === 201) {
            const body = response.json;
            return body.items
                ? new models_1.CourseEnrollmentsPage(body)
                : new models_1.CourseEnrollment(body);
        }
        // else 204 no exception or response body
    }
    /**
     * Fetches all course enrollments for the current user paging as necessary to get them all.
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<[Enrollment]>} the enrollment object
     */
    async fetchUsersCourseEnrollments({ limit = DEFAULT_FETCH_ALL_LIMIT } = {}) {
        let enrollments = [];
        let lastKey = undefined;
        do {
            const page = await this.fetchPageOfUsersCourseEnrollments({
                limit,
                lastKey
            });
            lastKey = page.lastKey;
            enrollments = enrollments.concat(page.items);
        } while (lastKey);
        return enrollments;
    }
    /**
     * Fetches a single page of course enrollments for the current user.
     * @param {Object} options - optional {limit, lastKey}
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<[EnrollmentPage]>} the enrollment object
     */
    async fetchPageOfUsersCourseEnrollments(options) {
        const query = {};
        options.limit && (query.limit = options.limit);
        options.lastKey && (query.lastKey = options.lastKey);
        return await this._makeCourseEnrollmentRequest('GET', null, null, query);
    }
    /**
     * Fetches a single enrollment by course id for the current user
     * @param {string} courseId - the course id of the enrollment to fetch.
     * @throws {MissingParamError} when the courseId is missing
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @throws {EnrollmentDoesNotExistError} when the enrollment cant be found.
     * @returns {Promise.<Enrollment>} the enrollment object
     */
    async fetchUsersCourseEnrollment(courseId) {
        this.validateMissingParams({ courseId });
        return await this._makeCourseEnrollmentRequest('GET', courseId, null, null);
    }
    /**
     * Creates a new course enrollment entry for the current user
     * @param {string} courseId
     * @throws {MissingParamError} when the enrollment object is missing
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @throws {EnrollmentBodyNotValidError} when the enrollment payload was invalid and could not be updated
     * @returns {Promise.<Enrollment>} the new enrollment.
     */
    async enrollUserInCourse(courseId) {
        this.validateMissingParams({ courseId });
        return await this._makeCourseEnrollmentRequest('PUT', courseId, {
            courseId
        }, null);
    }
    /**
     * Deletes an existing enrollment for the current user
     * @param {string} courseId - the id of the enrollment to delete.
     * @throws {MissingParamError} when the courseId is missing
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @throws {EnrollmentDoesNotExistError} when the enrollment cant be found.
     * @returns {Promise.<undefined>}
     */
    async unenrollUserFromCourse(courseId) {
        this.validateMissingParams({ courseId });
        return await this._makeCourseEnrollmentRequest('DELETE', courseId, null, null);
    }
    throwSpecificInvalidSubscriptionError(reason) {
        switch (reason) {
            case 'No subscription found':
                throw new SubscriptionNotFoundError_1.default(reason);
            case 'Subscription not active':
                throw new SubscriptionNotActiveError_1.default(reason);
            case 'Subscription not valid':
                throw new SubscriptionNotValidError_1.default(reason);
            default:
                throw new SubscriptionNotValidError_1.default(reason);
        }
    }
    validateMissingParams(args) {
        Object.keys(args).forEach(argName => {
            if (!args[argName]) {
                throw new client_library_1.MissingParamError(argName);
            }
        });
    }
    async translateDraftObjects({ draftIds, translate }) {
        const url = `${this.url}/api/drafts/translate`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify({ draftIds, translate }) }, [200, 404]);
        if (response.status === 404) {
            throw new DraftObjectsNotFoundError_1.default('Draft objects could not be found');
        }
        return response.json;
    }
    async fetchPastPapers(courseId) {
        const url = `${this.url}/api/past-papers/${courseId}`;
        const expectedResponses = [200, 404];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 404) {
            throw new PastPapersDoNotExistError_1.default(courseId);
        }
        return response.json;
    }
    async createPastPaper({ courseId, pastPaper }) {
        const url = `${this.url}/api/past-papers/${courseId}`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify(pastPaper)
        }, [200]);
        return response.json;
    }
    async batchDeletePastPapers({ pastPaperIds, courseId }) {
        const url = `${this.url}/api/past-papers/${courseId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'DELETE', body: JSON.stringify({ pastPaperIds }) }, [204]);
        return response.json;
    }
    async updatePastPaper({ id, courseId, pastPaper }) {
        const url = `${this.url}/api/past-papers/${courseId}`;
        const fields = Object.assign(Object.assign({}, pastPaper), { id });
        const response = await this.requestMaker.makeRequest(url, {
            method: 'PATCH',
            body: JSON.stringify(fields)
        }, [200]);
        if (response.status === 404) {
            throw new PastPapersDoNotExistError_1.default(courseId);
        }
        return response.json;
    }
    async fetchWordAdmin(wordId) {
        const response = await this.requestMaker.makeRequest(`${this.url}/api/admin/vocabulary/words/${wordId}`, {
            method: 'GET'
        }, [200, 404]);
        if (response.status === 404) {
            throw new WordNotFoundError_1.default(wordId);
        }
        return response.json;
    }
    async addWordAdmin(params) {
        const response = await this.requestMaker.makeRequest(`${this.url}/api/admin/vocabulary/words`, {
            method: 'POST',
            body: JSON.stringify(params)
        }, [200, 403]);
        if (response.status === 403) {
            throw new UnauthorizedError_1.default();
        }
        return response.json;
    }
    async updateWordAdmin(params) {
        const { wordId } = params, body = __rest(params, ["wordId"]);
        const response = await this.requestMaker.makeRequest(`${this.url}/api/admin/vocabulary/words/${wordId}`, {
            method: 'PATCH',
            body: JSON.stringify(body)
        }, [200, 404, 403]);
        if (response.status === 404) {
            throw new WordNotFoundError_1.default(wordId);
        }
        if (response.status === 403) {
            throw new UnauthorizedError_1.default();
        }
        return response.json;
    }
    async deleteWordAdmin(wordId) {
        const response = await this.requestMaker.makeRequest(`${this.url}/api/admin/vocabulary/words/${wordId}`, {
            method: 'DELETE'
        }, [200, 404, 403]);
        if (response.status === 404) {
            throw new WordNotFoundError_1.default(wordId);
        }
        if (response.status === 403) {
            throw new UnauthorizedError_1.default();
        }
    }
    async fetchAllWordsAdmin(params) {
        const queryParams = Object.entries(params)
            .filter(([_, value]) => !!value)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        const response = await this.requestMaker.makeRequest(`${this.url}/api/admin/vocabulary/words${queryParams ? `?${queryParams}` : ''}`, {
            method: 'GET'
        }, [200, 403]);
        if (response.status === 403) {
            throw new UnauthorizedError_1.default();
        }
        return response.json;
    }
    async linkCourseWordsAdmin(courseWordLinks) {
        const response = await this.requestMaker.makeRequest(`${this.url}/api/admin/vocabulary/course-words/link`, {
            method: 'POST',
            body: JSON.stringify({ courseWordLinks })
        }, [200, 404, 403]);
        if (response.status === 404) {
            throw new WordNotFoundError_1.default('');
        }
        if (response.status === 403) {
            throw new UnauthorizedError_1.default();
        }
        return response.json;
    }
    async unlinkCourseWordsAdmin(courseWordLinks) {
        const response = await this.requestMaker.makeRequest(`${this.url}/api/admin/vocabulary/course-words/unlink`, {
            method: 'POST',
            body: JSON.stringify({ courseWordLinks })
        }, [200, 403]);
        if (response.status === 403) {
            throw new UnauthorizedError_1.default();
        }
        return response.json;
    }
    async fetchCourseWords(courseId, params) {
        const queryParams = Object.entries(params)
            .filter(([_, value]) => !!value)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        const response = await this.requestMaker.makeRequest(`${this.url}/api/courses/${courseId}/vocabulary${queryParams ? `?${queryParams}` : ''}`, {
            method: 'GET'
        }, [200, 403, 400, 422]);
        if (response.status === 403) {
            throw new UnauthorizedError_1.default();
        }
        if (response.status === 400 || response.status === 422) {
            const body = response.json;
            throw new InvalidParamsError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    async fetchWords(wordIds) {
        const response = await this.requestMaker.makeRequest(`${this.url}/api/vocabulary/batch-get/words`, {
            method: 'POST',
            body: JSON.stringify({ wordIds })
        }, [200, 404, 400, 422]);
        if (response.status === 404) {
            throw new WordsNotFoundError_1.default(wordIds);
        }
        if (response.status === 400 || response.status === 422) {
            const body = response.json;
            throw new InvalidParamsError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    async fetchWord(wordId) {
        const response = await this.requestMaker.makeRequest(`${this.url}/api/vocabulary/words/${wordId}`, {
            method: 'GET'
        }, [200, 404]);
        if (response.status === 404) {
            throw new WordNotFoundError_1.default(wordId);
        }
        return response.json;
    }
}
exports.default = CourseServiceClient;
//# sourceMappingURL=CourseServiceClient.js.map